<template>
  <div class="irp-open-opportunities-cell">
    <RouterLink
      v-if="opportunityCount && opportunitiesRoute"
      :to="opportunitiesRoute"
      :aria-label="linkLabel"
      class="irp-open-opportunities-cell__link"
    >
      {{ opportunityCount }}
    </RouterLink>
    <span v-else class="irp-open-opportunities-cell__empty">
      {{ opportunityCount }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type { ICellRendererParams } from '@ag-grid-community/core';
import { pluralize } from 'humanize-plus';
import { computed } from 'vue';

import type { RecordOverviewPartnerType } from '@/types/individual_record';

import { IRP_TAB, IRP_TAB_ROUTES } from './irp';
import { useRecordRouteParams } from './useRecordRouteParams';

const { params } = defineProps<{
  params: ICellRendererParams<RecordOverviewPartnerType>;
}>();

const recordParams = useRecordRouteParams();

const opportunityCount = computed(
  () => params.data?.opportunities?.items?.length ?? 0,
);

const partnerName = computed(() => params.data?.partner_name ?? '');

const opportunitiesRoute = computed(() => {
  const partnerOrgId = params.data?.partner_organization_id;
  if (!partnerOrgId) return null;
  return {
    name: IRP_TAB_ROUTES[IRP_TAB.opportunities],
    params: recordParams.value,
    query: { partner_org_ids: String(partnerOrgId) },
  };
});

const linkLabel = computed(() => {
  const opportunities = pluralize(
    opportunityCount.value,
    'opportunity',
    'opportunities',
  );
  return `View ${opportunityCount.value} open ${opportunities} with ${partnerName.value}`;
});
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.irp-open-opportunities-cell {
  @apply flex items-center h-full w-full text-sm;
}

.irp-open-opportunities-cell__link {
  @apply font-bold text-secondary-text rounded-4;
}

.irp-open-opportunities-cell__link:hover,
.irp-open-opportunities-cell__link:focus {
  @apply text-primary-text-link-hover;
}

.irp-open-opportunities-cell__link:focus-visible {
  @apply outline-hidden outline-2 outline-neutral-border-focus;
}

.irp-open-opportunities-cell__empty {
  @apply text-neutral-text-weak;
}
</style>
